import { DicomImageSlice } from '../dicom/dicomImageSlice';
import { Modality } from '../dicom/modality';
import { PatientDataSet } from '../dicom/patientdataset';
import { Series } from '../dicom/series';
import { ChangeTracker } from '../changes/ChangeTracker';
import {
    PatientSnapshot,
    SyncStatus,
    TrackableObject,
} from '../changes/DomainChange';
import { newPatientId, PatientId } from '../changes/ids';
import { DoseMatrix } from './doseMatrix';
import { Image3D } from './image3d';
import { Plan } from './plan';
import { RigidRegistration } from './rigidRegistration';
import { StructureSet } from './structureset';
import { domainTrace } from '../diagnostics/domainTrace';

/**
 * Domain object for a Patient. This class captures all the data related to a patient.
 */

export type ImageRegistration = {
    image: Image3D;
    registrations: RigidRegistration[] | undefined;
};

export class Patient implements TrackableObject {
    /**
     * The DICOM data set for the patient
     */
    public dataSet: PatientDataSet;

    readonly trackableKind = 'patient' as const;

    /**
     * Internal id\
     * Unique runtime identifier that is generated when the {@link Patient} is constructed.
     */
    public readonly id: PatientId;

    private tracker: ChangeTracker | null = null;
    private _syncStatus: SyncStatus = 'downloaded';

    private readonly _images: Image3D[] = new Array<Image3D>();

    private readonly _doses: DoseMatrix[] = new Array<DoseMatrix>();

    private readonly _structureSets: StructureSet[] = new Array<StructureSet>();

    private readonly _plans: Plan[] = new Array<Plan>();

    private readonly _registrations: RigidRegistration[] =
        new Array<RigidRegistration>();

    get trackableId(): PatientId {
        return this.id;
    }

    get syncStatus(): SyncStatus {
        return this._syncStatus;
    }

    constructor(data: PatientDataSet, id: PatientId = newPatientId()) {
        this.dataSet = data;
        this.id = id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
        for (const ss of this._structureSets) {
            ss.attachTracker(tracker);
        }
    }

    captureSnapshot(): PatientSnapshot {
        return {
            id: this.id,
            dicomPatientId: this.patientId,
            dicomPatientName:
                typeof this.dataSet?.PatientName === 'string'
                    ? this.dataSet.PatientName
                    : null,
            structureSetCount: this._structureSets.length,
            syncStatus: this._syncStatus,
        };
    }

    getStructureSetById(id: string): StructureSet | undefined {
        return this._structureSets.find((s) => s.id === id);
    }

    public get patientId(): string {
        return this.dataSet.PatientID;
    }

    public addImage(image: Image3D) {
        domainTrace.patient('addImage', {
            imageUid: image.dicomUid,
            totalImages: this._images.length + 1,
        });
        this._images.push(image);
    }

    public get images(): ReadonlyArray<Image3D> {
        return this._images;
    }

    public getImage3DWithDicomUid(uid: string): Image3D | undefined {
        return this._images.find((value: Image3D): boolean => {
            return value.dicomUid === uid;
        });
    }

    public getImageSliceWithSopInstanceUid(
        uid: string
    ): DicomImageSlice | undefined {
        for (const image of this._images) {
            const slice = image.imageSlices.find(
                (value: DicomImageSlice): boolean => {
                    return value.sopInstanceUid === uid;
                }
            );

            if (slice) {
                return slice;
            }
        }
        return undefined;
    }

    public addDose(dose: DoseMatrix) {
        domainTrace.patient('addDose', {
            doseUid: dose.sopInstanceUid,
            totalDoses: this._doses.length + 1,
        });
        this._doses.push(dose);
    }

    public get doses(): ReadonlyArray<DoseMatrix> {
        return this._doses;
    }

    public getDoseWithSopInstanceUid(uid: string): DoseMatrix | undefined {
        return this._doses.find((value: DoseMatrix): boolean => {
            return value.sopInstanceUid === uid;
        });
    }

    public removeDose(dose: DoseMatrix): ReadonlyArray<DoseMatrix> {
        domainTrace.patient('removeDose', {
            doseUid: dose.sopInstanceUid,
            totalDoses:
                this._doses.length - (this._doses.includes(dose) ? 1 : 0),
        });
        const index = this._doses.indexOf(dose);
        if (index > -1) {
            this._doses.splice(index, 1);
        }

        for (const plan of this._plans) {
            if (plan.doseMatrix === dose) {
                plan.doseMatrix = null;
            }
        }

        return this.doses;
    }

    public addStructureSet(structureSet: StructureSet): void {
        domainTrace.patient('addStructureSet', {
            ssId: structureSet.id,
            totalStructureSets: this._structureSets.length + 1,
        });
        this._structureSets.push(structureSet);
        if (this.tracker) {
            structureSet.attachTracker(this.tracker);
            this.tracker.markChanged(structureSet, 'created');
            this.tracker.markChanged(this);
        }
    }

    public get structureSets(): ReadonlyArray<StructureSet> {
        return this._structureSets;
    }

    public addPlan(plan: Plan) {
        domainTrace.patient('addPlan', {
            planUid: plan.sopInstanceUid,
            totalPlans: this._plans.length + 1,
        });
        this._plans.push(plan);
    }

    public get plans(): ReadonlyArray<Plan> {
        return this._plans;
    }

    public getPlanWithSopInstanceUid(uid: string): Plan | undefined {
        return this._plans.find((plan) => {
            return plan.sopInstanceUid === uid;
        });
    }

    public addRegistration(registration: RigidRegistration) {
        domainTrace.patient('addRegistration', {
            regUid: registration.sopInstanceUid,
            totalRegistrations: this._registrations.length + 1,
        });
        this._registrations.push(registration);
    }

    public removeImage(image: Image3D): ReadonlyArray<Image3D> {
        domainTrace.patient('removeImage', {
            imageUid: image.dicomUid,
            totalImages:
                this._images.length - (this._images.includes(image) ? 1 : 0),
        });
        const index = this._images.indexOf(image);
        if (index > -1) {
            this._images.splice(index, 1);
        }

        for (const ss of this._structureSets) {
            if (ss.image === image) {
                ss.image = null;
            }
        }

        return this.images;
    }

    public removeStructureSet(
        structureSet: StructureSet
    ): ReadonlyArray<StructureSet> {
        domainTrace.patient('removeStructureSet', {
            ssId: structureSet.id,
            totalStructureSets:
                this._structureSets.length -
                (this._structureSets.includes(structureSet) ? 1 : 0),
        });
        const index = this._structureSets.indexOf(structureSet);
        if (index > -1) {
            this._structureSets.splice(index, 1);
        }

        for (const plan of this._plans) {
            if (plan.structureSet === structureSet) {
                plan.structureSet = null;
            }
        }
        for (const dose of this._doses) {
            if (dose.structureSet === structureSet) {
                dose.structureSet = null;
            }
        }

        if (this.tracker) {
            this.tracker.markChanged(structureSet, 'deleted');
            this.tracker.markChanged(this);
        }

        return this.structureSets;
    }

    public removeRegistration(
        registration: RigidRegistration
    ): ReadonlyArray<RigidRegistration> {
        domainTrace.patient('removeRegistration', {
            regUid: registration.sopInstanceUid,
            totalRegistrations:
                this._registrations.length -
                (this._registrations.includes(registration) ? 1 : 0),
        });
        const index = this._registrations.indexOf(registration);
        if (index > -1) {
            this._registrations.splice(index, 1);
        }
        return this.registrations;
    }

    public get registrations(): ReadonlyArray<RigidRegistration> {
        return this._registrations;
    }

    /**
     * Returns structure sets that reference the given image.
     * Used to warn the user before deleting an image.
     */
    public getAffectedStructureSets(image: Image3D): StructureSet[] {
        return this._structureSets.filter((ss) => ss.image === image);
    }

    public getRegistrationWithSopInstanceUid(
        uid: string
    ): RigidRegistration | undefined {
        return this._registrations.find((reg) => reg.sopInstanceUid === uid);
    }

    /**
     * Returns the rigid registrations that link two images by matching
     * their Frames of Reference UIDs in either direction.
     *
     * Returns an empty array when both images share the same Frame of
     * Reference (no registration needed) or when either FOR UID is
     * missing.
     *
     * @param source - The first image
     * @param target - The second image
     */
    public findRegistrationsBetween(
        source: Image3D,
        target: Image3D
    ): RigidRegistration[] {
        const srcFor = source.frameOfReferenceUid;
        const tgtFor = target.frameOfReferenceUid;
        if (!srcFor || !tgtFor || srcFor === tgtFor) {
            return [];
        }
        return this._registrations.filter(
            (reg) =>
                (reg.sourceFrameOfReferenceUid === srcFor &&
                    reg.targetFrameOfReferenceUid === tgtFor) ||
                (reg.sourceFrameOfReferenceUid === tgtFor &&
                    reg.targetFrameOfReferenceUid === srcFor)
        );
    }

    public getSecondaryImages(sourceImage: Image3D) {
        const result: { image: Image3D; registrations: RigidRegistration[] }[] =
            [];

        for (const image of this._images) {
            if (image === sourceImage) {
                continue;
            }

            const registrations = this.findRegistrationsBetween(
                sourceImage,
                image
            );
            result.push({ image, registrations });
        }

        return result;
    }

    /**
     * Returns secondary images that are eligible for blending with the
     * given primary image. An image is blendable if it either shares
     * the same Frame of Reference or has at least one registration
     * linking it to the primary.
     *
     * Registrations in either direction are accepted because the
     * rendering pipeline inverts the transformation matrix as needed
     * (see RigidRegistration.getTransformFromTo).
     *
     * @param primaryImage - The primary image currently loaded
     */
    public getBlendableSecondaryImages(
        primaryImage: Image3D
    ): ImageRegistration[] {
        const result: ImageRegistration[] = [];

        for (const imgReg of this.getSecondaryImages(primaryImage)) {
            const sharesFOR =
                primaryImage.frameOfReferenceUid ===
                imgReg.image.frameOfReferenceUid;
            const hasRegistrations = (imgReg.registrations ?? []).length > 0;

            if (sharesFOR || hasRegistrations) {
                result.push(imgReg);
            }
        }

        return result;
    }

    /**
     * Gets all unique image registrations for a primary image,
     * excluding the primary image itself and deduplicating by series UID.
     *
     * @param primaryImage - The primary image to get registrations for
     * @returns Array of unique ImageRegistration objects, deduplicated by series UID
     */
    public getAllRegistrationsForPrimary(
        primaryImage: Image3D
    ): ImageRegistration[] {
        const allRegistrations = this.getSecondaryImages(primaryImage);
        const primarySeriesUID = primaryImage.seriesUid;

        // Filter out the primary image and deduplicate by series UID
        const seen = new Set<string>();
        const result: ImageRegistration[] = [];

        for (const imgReg of allRegistrations) {
            const seriesUid = imgReg.image.seriesUid;
            if (seriesUid !== primarySeriesUID && !seen.has(seriesUid)) {
                seen.add(seriesUid);
                result.push({
                    image: imgReg.image,
                    registrations: imgReg.registrations,
                });
            }
        }

        return result;
    }

    /**
     * Gets priority registrations - those that have valid registration data.
     * Excludes the primary image and deduplicates by series UID.
     *
     * @param primaryImage - The primary image to filter against
     * @returns Array of ImageRegistration objects that have valid registration data
     */
    public getPriorityRegistrations(
        primaryImage: Image3D
    ): ImageRegistration[] {
        const allRegistrations =
            this.getAllRegistrationsForPrimary(primaryImage);

        return allRegistrations.filter((imgReg) => {
            // Must have registrations and not be the primary image
            return imgReg.registrations && imgReg.registrations.length > 0;
        });
    }

    /**
     * Gets completed registrations that are not in the priority list.
     * These are registrations that have registration data but are not
     * among the priority registrations.
     *
     * @param primaryImage - The primary image to filter against
     * @param priorityRegistrations - Array of priority registrations to exclude
     * @returns Array of ImageRegistration objects that are completed but not priority
     */
    public getCompletedRegistrations(
        primaryImage: Image3D,
        priorityRegistrations: ImageRegistration[]
    ): ImageRegistration[] {
        const allRegistrations =
            this.getAllRegistrationsForPrimary(primaryImage);

        // Create a set of priority series UIDs for efficient lookup
        const prioritySeriesUIDs = new Set(
            priorityRegistrations.map((img) => img.image.seriesUid)
        );

        return allRegistrations.filter((imgReg) => {
            // Must have registrations, not be the primary image, and not be in priority list
            return (
                imgReg.registrations &&
                imgReg.registrations.length > 0 &&
                !prioritySeriesUIDs.has(imgReg.image.seriesUid)
            );
        });
    }

    public getStructureSetWithSopInstanceUid(
        uid: string
    ): StructureSet | undefined {
        return this._structureSets.find((value: StructureSet): boolean => {
            return value.dicomStructureSet.sopInstanceUid === uid;
        });
    }

    /**
     * Retrieves a domain series item (ImageSlice, DoseMatrix, StructureSet, RigidRegistration, or Plan)
     * from the given series based on the SOP Instance UID.
     *
     * @param series - The series to search within
     * @param sopInstanceUid - The SOP Instance UID to search for
     * @returns The matching series item if found, otherwise undefined
     * @throws {Error} If the series modality is not supported
     */
    public getSeriesItemWithSopInstanceUid(
        series: Series,
        sopInstanceUid: string
    ):
        | DicomImageSlice
        | DoseMatrix
        | StructureSet
        | RigidRegistration
        | Plan
        | undefined {
        if (!series) {
            return undefined;
        }

        const modality = series.seriesModule.modality;

        switch (modality) {
            case Modality.CT:
            case Modality.MR:
            case Modality.PT:
                return this.getImageSliceWithSopInstanceUid(sopInstanceUid);
            case Modality.RTDOSE:
                return this.getDoseWithSopInstanceUid(sopInstanceUid);
            case Modality.RTSTRUCT:
                return this.getStructureSetWithSopInstanceUid(sopInstanceUid);
            case Modality.RTPLAN:
                return this.getPlanWithSopInstanceUid(sopInstanceUid);
            case Modality.REG:
                return this.getRegistrationWithSopInstanceUid(sopInstanceUid);
            default:
                throw new Error(`Unsupported modality: ${modality}`);
        }
    }
}
